(function () {
  var initialized = new WeakSet();
  var reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

  function initAll(context) {
    var scope = context || document;
    var sections = scope.matches && scope.matches("[data-af-section]")
      ? [scope]
      : Array.prototype.slice.call(scope.querySelectorAll("[data-af-section]"));

    sections.forEach(initSection);
  }

  function initSection(root) {
    if (!root || initialized.has(root)) return;
    initialized.add(root);
    document.body.classList.add("af-commercial-landing-active");

    initScrolling(root);
    initModal(root);
    initTabs(root);
    initReveal(root);
    initTimeline(root);
    initComparisons(root);
    initLightbox(root);
    initSliders(root);
    initParallax(root);
  }

  function initScrolling(root) {
    var triggers = root.querySelectorAll("[data-af-scroll], [data-af-scroll-to]");

    triggers.forEach(function (trigger) {
      trigger.addEventListener("click", function (event) {
        var selector = trigger.getAttribute("data-af-scroll-to");

        if (!selector && trigger.hash) {
          selector = trigger.hash;
        }

        if (!selector || selector.charAt(0) !== "#") return;

        var target = root.querySelector(selector) || document.querySelector(selector);
        if (!target) return;

        event.preventDefault();
        target.scrollIntoView({
          behavior: reducedMotionQuery.matches ? "auto" : "smooth",
          block: "start"
        });

        var focusTarget = getFirstFocusable(target) || target;
        if (!focusTarget.hasAttribute("tabindex") && focusTarget === target) {
          focusTarget.setAttribute("tabindex", "-1");
        }
        window.setTimeout(function () {
          focusTarget.focus({ preventScroll: true });
        }, reducedMotionQuery.matches ? 0 : 450);
      });
    });
  }

  function initModal(root) {
    var modal = root.querySelector("[data-af-modal]");
    if (!modal) return;

    var panel = modal.querySelector("[data-af-modal-panel]");
    var openButtons = root.querySelectorAll("[data-af-open-modal]");
    var closeButtons = modal.querySelectorAll("[data-af-close-modal]");
    var lastFocused = null;

    function openModal() {
      lastFocused = document.activeElement;
      modal.hidden = false;
      document.body.classList.add("af-modal-open");

      window.setTimeout(function () {
        var first = getFirstFocusable(panel) || panel;
        first.focus();
      }, 20);
    }

    function closeModal() {
      modal.hidden = true;
      document.body.classList.remove("af-modal-open");
      if (lastFocused && typeof lastFocused.focus === "function") {
        lastFocused.focus();
      }
    }

    openButtons.forEach(function (button) {
      button.addEventListener("click", openModal);
    });

    closeButtons.forEach(function (button) {
      button.addEventListener("click", closeModal);
    });

    modal.addEventListener("keydown", function (event) {
      if (modal.hidden) return;

      if (event.key === "Escape") {
        event.preventDefault();
        closeModal();
        return;
      }

      if (event.key === "Tab") {
        trapFocus(event, panel);
      }
    });
  }

  function initTabs(root) {
    var tabGroups = root.querySelectorAll("[data-af-tabs]");

    tabGroups.forEach(function (group) {
      var tabs = Array.prototype.slice.call(group.querySelectorAll("[data-af-tab]"));
      var panels = Array.prototype.slice.call(group.querySelectorAll("[data-af-panel]"));

      function activate(tab) {
        var panelId = tab.getAttribute("aria-controls");

        tabs.forEach(function (item) {
          var isActive = item === tab;
          item.classList.toggle("is-active", isActive);
          item.setAttribute("aria-selected", isActive ? "true" : "false");
          item.setAttribute("tabindex", isActive ? "0" : "-1");
        });

        panels.forEach(function (panel) {
          var isActive = panel.id === panelId;
          panel.classList.toggle("is-active", isActive);
          panel.hidden = !isActive;
        });
      }

      tabs.forEach(function (tab, index) {
        tab.setAttribute("tabindex", index === 0 ? "0" : "-1");

        tab.addEventListener("click", function () {
          activate(tab);
        });

        tab.addEventListener("keydown", function (event) {
          var nextIndex = index;

          if (event.key === "ArrowRight") nextIndex = (index + 1) % tabs.length;
          if (event.key === "ArrowLeft") nextIndex = (index - 1 + tabs.length) % tabs.length;
          if (event.key === "Home") nextIndex = 0;
          if (event.key === "End") nextIndex = tabs.length - 1;

          if (nextIndex !== index) {
            event.preventDefault();
            tabs[nextIndex].focus();
            activate(tabs[nextIndex]);
          }
        });
      });
    });
  }

  function initReveal(root) {
    var elements = Array.prototype.slice.call(root.querySelectorAll(".af-reveal"));

    if (!elements.length) return;

    if (reducedMotionQuery.matches || !("IntersectionObserver" in window)) {
      elements.forEach(function (element) {
        element.classList.add("is-visible");
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.16,
      rootMargin: "0px 0px -8% 0px"
    });

    elements.forEach(function (element) {
      observer.observe(element);
    });
  }

  function initTimeline(root) {
    var timeline = root.querySelector("[data-af-timeline]");
    if (!timeline) return;

    var items = Array.prototype.slice.call(timeline.querySelectorAll("[data-af-timeline-item]"));

    function updateProgress() {
      var rect = timeline.getBoundingClientRect();
      var viewportTrigger = window.innerHeight * 0.68;
      var raw = (viewportTrigger - rect.top) / Math.max(rect.height, 1);
      var progress = Math.max(0, Math.min(1, raw));
      timeline.style.setProperty("--af-timeline-progress", (progress * 100).toFixed(2) + "%");
    }

    updateProgress();
    window.addEventListener("scroll", updateProgress, { passive: true });
    window.addEventListener("resize", updateProgress);

    if (!("IntersectionObserver" in window)) {
      items.forEach(function (item) {
        item.classList.add("is-active");
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        entry.target.classList.toggle("is-active", entry.isIntersecting);
      });
    }, {
      threshold: 0.55
    });

    items.forEach(function (item) {
      observer.observe(item);
    });
  }

  function initComparisons(root) {
    var comparisons = root.querySelectorAll("[data-af-comparison]");

    comparisons.forEach(function (comparison) {
      var range = comparison.querySelector("[data-af-comparison-range]");
      var before = comparison.querySelector("[data-af-comparison-before]");
      var handle = comparison.querySelector("[data-af-comparison-handle]");

      if (!range || !before || !handle) return;

      function update() {
        var value = Math.max(0, Math.min(100, Number(range.value) || 50));
        var width = value + "%";
        before.style.width = width;
        before.style.setProperty("--af-compare-width", String(value / 100));
        handle.style.left = width;
      }

      range.addEventListener("input", update);
      update();
    });
  }

  function initLightbox(root) {
    var modal = root.querySelector("[data-af-lightbox-modal]");
    if (!modal) return;

    var panel = modal.querySelector("[data-af-lightbox-panel]");
    var image = modal.querySelector("[data-af-lightbox-image]");
    var title = modal.querySelector("[data-af-lightbox-title]");
    var caption = modal.querySelector("[data-af-lightbox-caption]");
    var closeButtons = modal.querySelectorAll("[data-af-lightbox-close]");
    var triggers = root.querySelectorAll("[data-af-lightbox]");
    var lastFocused = null;

    function open(trigger) {
      var imageUrl = trigger.getAttribute("data-image");
      if (!imageUrl) return;

      lastFocused = document.activeElement;
      image.src = imageUrl;
      image.alt = trigger.getAttribute("data-title") || "";
      title.textContent = trigger.getAttribute("data-title") || "";
      caption.textContent = trigger.getAttribute("data-caption") || "";
      modal.hidden = false;
      document.body.classList.add("af-modal-open");

      window.setTimeout(function () {
        panel.focus();
      }, 20);
    }

    function close() {
      modal.hidden = true;
      image.removeAttribute("src");
      document.body.classList.remove("af-modal-open");
      if (lastFocused && typeof lastFocused.focus === "function") {
        lastFocused.focus();
      }
    }

    triggers.forEach(function (trigger) {
      trigger.addEventListener("click", function () {
        open(trigger);
      });
    });

    closeButtons.forEach(function (button) {
      button.addEventListener("click", close);
    });

    modal.addEventListener("keydown", function (event) {
      if (modal.hidden) return;

      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }

      if (event.key === "Tab") {
        trapFocus(event, panel);
      }
    });
  }

  function initSliders(root) {
    var sliders = root.querySelectorAll("[data-af-slider]");

    sliders.forEach(function (slider) {
      var track = slider.querySelector("[data-af-slider-track]");
      var slides = Array.prototype.slice.call(slider.querySelectorAll("[data-af-slide]"));
      var prev = slider.querySelector("[data-af-slider-prev]");
      var next = slider.querySelector("[data-af-slider-next]");
      var dotsWrap = slider.querySelector("[data-af-slider-dots]");
      var index = 0;
      var startX = 0;
      var currentX = 0;
      var isTouching = false;

      if (!track || !slides.length) return;

      function goTo(nextIndex) {
        index = (nextIndex + slides.length) % slides.length;
        track.style.transform = "translateX(" + (-index * 100) + "%)";

        slides.forEach(function (slide, slideIndex) {
          slide.setAttribute("aria-hidden", slideIndex === index ? "false" : "true");
        });

        if (dotsWrap) {
          Array.prototype.slice.call(dotsWrap.children).forEach(function (dot, dotIndex) {
            dot.classList.toggle("is-active", dotIndex === index);
            dot.setAttribute("aria-current", dotIndex === index ? "true" : "false");
          });
        }
      }

      if (dotsWrap) {
        dotsWrap.innerHTML = "";
        slides.forEach(function (_, slideIndex) {
          var dot = document.createElement("button");
          dot.type = "button";
          dot.setAttribute("aria-label", "Show testimonial " + (slideIndex + 1));
          dot.addEventListener("click", function () {
            goTo(slideIndex);
          });
          dotsWrap.appendChild(dot);
        });
      }

      if (prev) {
        prev.addEventListener("click", function () {
          goTo(index - 1);
        });
      }

      if (next) {
        next.addEventListener("click", function () {
          goTo(index + 1);
        });
      }

      slider.addEventListener("touchstart", function (event) {
        if (!event.touches || !event.touches.length) return;
        isTouching = true;
        startX = event.touches[0].clientX;
        currentX = startX;
      }, { passive: true });

      slider.addEventListener("touchmove", function (event) {
        if (!isTouching || !event.touches || !event.touches.length) return;
        currentX = event.touches[0].clientX;
      }, { passive: true });

      slider.addEventListener("touchend", function () {
        if (!isTouching) return;
        var delta = currentX - startX;
        isTouching = false;

        if (Math.abs(delta) > 44) {
          goTo(delta < 0 ? index + 1 : index - 1);
        }
      });

      goTo(0);
    });
  }

  function initParallax(root) {
    if (reducedMotionQuery.matches) return;

    var layers = Array.prototype.slice.call(root.querySelectorAll(".af-hero__media, .af-final__media"));
    if (!layers.length) return;

    var ticking = false;

    function update() {
      layers.forEach(function (layer) {
        var parent = layer.parentElement;
        if (!parent) return;

        var rect = parent.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;

        var offset = Math.max(-44, Math.min(44, rect.top * -0.055));
        layer.style.setProperty("--af-parallax-y", offset.toFixed(2) + "px");
      });

      ticking = false;
    }

    function requestUpdate() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    update();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
  }

  function getFocusable(container) {
    if (!container) return [];

    return Array.prototype.slice.call(container.querySelectorAll([
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "[tabindex]:not([tabindex='-1'])"
    ].join(","))).filter(function (element) {
      return element.offsetParent !== null || element === document.activeElement;
    });
  }

  function getFirstFocusable(container) {
    return getFocusable(container)[0];
  }

  function trapFocus(event, container) {
    var focusable = getFocusable(container);
    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });
})();
